import React, { useState } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import { getAuthHeader } from '../services/otpAuthService';
import L from 'leaflet';
import { MapPin, Building2, Mail, Phone, AlertCircle, Copy, Check, MousePointer2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix per i marker di Leaflet
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Icona personalizzata blu per l'associato
const blueIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="32" height="48">
      <path fill="#2563eb" stroke="#1e40af" stroke-width="1.5" d="M12 0C7.03 0 3 4.03 3 9c0 7.5 9 18 9 18s9-10.5 9-18c0-4.97-4.03-9-9-9z"/>
      <circle cx="12" cy="9" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48],
});

// Icona personalizzata rossa per la posizione selezionata dall'utente
const redIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="32" height="48">
      <path fill="#dc2626" stroke="#991b1b" stroke-width="1.5" d="M12 0C7.03 0 3 4.03 3 9c0 7.5 9 18 9 18s9-10.5 9-18c0-4.97-4.03-9-9-9z"/>
      <circle cx="12" cy="9" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48],
});

interface AssociateData {
  nominativo: string;
  partitaIVA: string;
  email: string;
  telefono?: string;
  indirizzo?: string;
  civico?: string;
  cap?: string;
  citta: string;
  provincia: string;
  regione: string;
  coordinates?: {
    lat?: number;
    lng?: number;
    geocoded?: boolean;
    geocodedAddress?: string;
    geocodeQuality?: 'none' | 'city' | 'street' | 'exact';
  };
}

interface AssociateLocationMapProps {
  associate: AssociateData;
  /** `id` della sede da GET /api/auth/my-sedi: `associate_<id>` per la sede legale, `_id` della Sede per una secondaria. */
  sedeId: string;
  /** Chiamata dopo un invio riuscito, per tenere il punto inviato anche cambiando sede e tornando indietro. */
  onInviata?: (sedeId: string, coords: { lat: number; lng: number }) => void;
}

const SESSIONE_SCADUTA = 'Sessione scaduta: ricarica la pagina e riprova';

/**
 * Componente per gestire i click sulla mappa
 */
const MapClickHandler: React.FC<{ onLocationClick: (lat: number, lng: number) => void }> = ({ onLocationClick }) => {
  useMapEvents({
    click: (e) => {
      onLocationClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

/**
 * Componente mappa per visualizzare la posizione dell'associato nell'Area Riservata
 */
const AssociateLocationMap: React.FC<AssociateLocationMapProps> = ({ associate, sedeId, onInviata }) => {
  const [clickedCoords, setClickedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [copiedCoords, setCopiedCoords] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [visualCoords, setVisualCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Verifica se ci sono coordinate valide
  const hasCoordinates = associate.coordinates?.geocoded &&
                         associate.coordinates?.lat &&
                         associate.coordinates?.lng;

  // Invia la richiesta di correzione per QUESTA sede (sedeId): lo studio lo ricava il server dal token.
  const handleSendCorrection = async () => {
    if (!clickedCoords) return;

    setSending(true);
    setSendError(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
      const nuove = { lat: clickedCoords.lat, lng: clickedCoords.lng };

      const { data } = await axios.post(
        `${API_BASE_URL}/auth/send-coordinate-correction`,
        {
          sedeId,
          oldCoordinates: {
            lat: associate.coordinates?.lat,
            lng: associate.coordinates?.lng
          },
          newCoordinates: nuove
        },
        { headers: getAuthHeader() as any }
      );

      if (data.success) {
        setSendSuccess(true);
        setShowConfirmModal(false);
        setVisualCoords(nuove);
        setClickedCoords(null);
        onInviata?.(sedeId, nuove);
      } else {
        setSendError(data.message || 'Errore durante l\'invio');
      }
    } catch (error: any) {
      console.error('Errore invio correzione:', error);
      const status = error?.response?.status;
      if (status === 401) {
        setSendError(SESSIONE_SCADUTA);
      } else if (status) {
        setSendError(error.response.data?.message || 'Errore durante l\'invio');
      } else {
        setSendError('Errore di connessione. Riprova più tardi.');
      }
    } finally {
      setSending(false);
    }
  };

  if (!hasCoordinates) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl p-8 text-center border-2 border-dashed border-gray-300 dark:border-gray-700">
        <MapPin className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
          Posizione non disponibile
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500">
          La sede non è stata ancora geocodificata o non fa ancora parte della Rete Certificata
        </p>
      </div>
    );
  }

  // Garantito che coordinates esiste grazie al check hasCoordinates
  const coords = associate.coordinates!;
  const currentPosition: [number, number] = visualCoords
    ? [visualCoords.lat, visualCoords.lng]
    : [coords.lat!, coords.lng!];
  const center: [number, number] = currentPosition;

  // Determina lo zoom in base alla qualità della geocodifica
  const getZoomLevel = () => {
    switch (coords.geocodeQuality) {
      case 'exact':
        return 18; // Zoom massimo per indirizzo esatto (livello strada)
      case 'street':
        return 17; // Zoom alto per via
      case 'city':
        return 15; // Zoom medio per città
      default:
        return 14;
    }
  };

  const zoom = getZoomLevel();

  // Costruisci l'indirizzo completo
  const fullAddress = [
    associate.indirizzo,
    associate.civico,
    associate.cap,
    associate.citta,
    associate.provincia && `(${associate.provincia})`
  ].filter(Boolean).join(', ');

  return (
    <div className="relative">
      {/* Badge qualità geocodifica */}
      {coords.geocodeQuality && coords.geocodeQuality !== 'exact' && (
        <div className="absolute top-3 right-3 z-[1000]">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200 shadow-md">
            {coords.geocodeQuality === 'city' && '📍 Posizione approssimativa (città)'}
            {coords.geocodeQuality === 'street' && '📍 Posizione approssimativa (via)'}
          </span>
        </div>
      )}

      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '400px', width: '100%', borderRadius: '0.75rem', position: 'relative', zIndex: 1 }}
        scrollWheelZoom={true}
        className="shadow-lg"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Gestore click sulla mappa */}
        <MapClickHandler onLocationClick={(lat, lng) => setClickedCoords({ lat, lng })} />

        {/* Marker blu - Posizione attuale (o aggiornata visivamente) */}
        <Marker position={currentPosition} icon={blueIcon}>
          <Popup maxWidth={300} className="custom-popup">
            <div className="p-3">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-gray-900 mb-1">
                    {associate.nominativo}
                  </h3>
                  <p className="text-xs text-gray-600">
                    P.IVA: {associate.partitaIVA}
                  </p>
                </div>
              </div>

              <div className="space-y-2 border-t border-gray-200 pt-3">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-700">
                    {fullAddress}
                  </p>
                </div>

                {associate.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <a 
                      href={`mailto:${associate.email}`}
                      className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {associate.email}
                    </a>
                  </div>
                )}

                {associate.telefono && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <a 
                      href={`tel:${associate.telefono}`}
                      className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      {associate.telefono}
                    </a>
                  </div>
                )}
              </div>

              {coords.geocodedAddress && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-[10px] text-gray-500 italic">
                    {coords.geocodedAddress}
                  </p>
                </div>
              )}
            </div>
          </Popup>
        </Marker>

        {/* Marker rosso - Nuova posizione selezionata dall'utente */}
        {clickedCoords && (
          <Marker position={[clickedCoords.lat, clickedCoords.lng]} icon={redIcon}>
            <Popup maxWidth={250}>
              <div className="p-2">
                <h4 className="font-semibold text-sm text-red-600 mb-2">
                  📍 Nuova Posizione Selezionata
                </h4>
                <p className="text-xs text-gray-600 mb-1">
                  Lat: {clickedCoords.lat.toFixed(7)}
                </p>
                <p className="text-xs text-gray-600">
                  Lng: {clickedCoords.lng.toFixed(7)}
                </p>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Pannello informativo - Posizione non esatta */}
      <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
              Posizione non esatta?
            </h4>
            <p className="text-xs text-blue-800 dark:text-blue-200 mb-3">
              Se la posizione mostrata sulla mappa non corrisponde esattamente alla tua sede, puoi inviarci le coordinate precise:
            </p>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
                <MousePointer2 className="w-4 h-4" />
                <span className="font-medium">1. Clicca sulla mappa nel punto esatto della tua sede</span>
              </div>

              {clickedCoords && (
                <div className="bg-white dark:bg-gray-800 rounded-md p-3 border border-blue-300 dark:border-blue-700 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      Coordinate selezionate:
                    </span>
                    <button
                      onClick={() => {
                        const coordsText = `${clickedCoords.lat.toFixed(7)}, ${clickedCoords.lng.toFixed(7)}`;
                        navigator.clipboard.writeText(coordsText);
                        setCopiedCoords(true);
                        setTimeout(() => setCopiedCoords(false), 2000);
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
                    >
                      {copiedCoords ? (
                        <>
                          <Check className="w-3 h-3" />
                          Copiate!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          Copia
                        </>
                      )}
                    </button>
                  </div>
                  <div className="font-mono text-xs text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-900 px-2 py-1.5 rounded">
                    Lat: {clickedCoords.lat.toFixed(7)}, Lng: {clickedCoords.lng.toFixed(7)}
                  </div>

                  {/* Bottone Invia Nuova Posizione */}
                  <button
                    onClick={() => setShowConfirmModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg"
                  >
                    <Mail className="w-4 h-4" />
                    Invia Nuova Posizione
                  </button>
                </div>
              )}

              {/* Messaggio di successo (inline ridotto, il popup modale è sotto) */}

              {/* Messaggio di errore */}
              {sendError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <p className="text-xs font-medium text-red-800 dark:text-red-200">
                      {sendError}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modale di Conferma */}
      {showConfirmModal && clickedCoords && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
                  Conferma Invio Nuova Posizione
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Stai per inviare la nuova posizione di questa sede.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Sede:</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100 text-right">{associate.nominativo}</span>
              </div>
              {fullAddress && (
                <div className="flex justify-between gap-3 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Indirizzo:</span>
                  <span className="text-gray-900 dark:text-gray-100 text-right">{fullAddress}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">P.IVA:</span>
                <span className="font-mono text-gray-900 dark:text-gray-100">{associate.partitaIVA}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Nuove coordinate:</p>
                <div className="font-mono text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                  Lat: {clickedCoords.lat.toFixed(7)}<br />
                  Lng: {clickedCoords.lng.toFixed(7)}
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              La nuova posizione verrà applicata entro qualche minuto e riceverai una email di conferma.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={sending}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={handleSendCorrection}
                disabled={sending}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Invio...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Conferma e Invia
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Successo */}
      {sendSuccess && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-5">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-2">
                Richiesta Inviata con Successo
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                La nuova posizione della sede <strong>{associate.nominativo}</strong> verrà applicata entro qualche minuto.
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Importante
                  </p>
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    Se hai sbagliato punto puoi inviarlo di nuovo entro qualche minuto: vale l'ultima posizione inviata per questa sede.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-5">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Riceverai una email di conferma quando la posizione sarà applicata.
              </p>
            </div>

            <button
              onClick={() => setSendSuccess(false)}
              className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
            >
              Ho capito
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssociateLocationMap;

